// Banner de promos de «Elige el teléfono» en tabla de tres columnas: qué promo, a qué aplica y qué da.
// Lo comparten el verde (venta) y el morado (crédito) para que se lean igual lado a lado.
import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { COUPON_CHIP, PROMOTION_GROUP_TONE } from '../promotionConstants';
import type { BenefitBubble } from '../promotionSlots';

const tone = (group: keyof typeof PROMOTION_GROUP_TONE): string =>
    `${PROMOTION_GROUP_TONE[group].box} ${PROMOTION_GROUP_TONE[group].text}`;

/** Chip con el color del grupo de su palanca. */
const CHIP_TONE: Record<NonNullable<BenefitBubble['tone']>, string> = {
    coupon: COUPON_CHIP,
    credit: tone('credit'),
    gift: tone('gift'),
    benefit: tone('benefit'),
};

export type PromoBannerTone = 'sale' | 'credit';

export interface PromoBannerChip {
    key: string;
    label: string;
    icon?: LucideIcon;
    /** `coupon` pinta el chip en pizarra en vez del color del banner. */
    /** Color del grupo de la palanca; sin tono, el del banner (verde = baja el precio). */
    tone?: BenefitBubble['tone'];
}

export interface PromoBannerRow {
    key: string;
    name: string;
    /** Chip junto al nombre, p. ej. «Solo con crédito SureKeep». */
    badge?: string;
    detail?: string;
    /** Condiciones del cliente, en una línea. */
    note?: string;
    reach: string;
    reachDetail?: string;
    chips: PromoBannerChip[];
    chipsDetail?: string;
}

const TONE: Record<PromoBannerTone, { box: string; text: string; chip: string }> = {
    // Contenedor neutro: el verde queda para lo que baja el precio, en cada chip.
    sale: {
        box: 'border-border bg-muted/40',
        text: 'text-foreground',
        chip: 'bg-success-soft text-success',
    },
    credit: {
        box: 'border-promo-soft-border bg-promo-soft',
        text: 'text-promo',
        chip: 'bg-promo-soft text-promo',
    },
};

const HEAD =
    'px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground';
const CELL = 'min-w-0 border-t border-border/60 px-3 pb-2.5 pt-2';
/** Renglones del mismo alto: cada línea se trunca con tooltip y se reserva aunque esté vacía. */
const LINE_MAIN = 'h-[18px] truncate font-bold leading-[18px]';
const LINE_SUB = 'h-4 truncate text-[11px] leading-4 text-muted-foreground';

export function PromoBanner({
    tone,
    label,
    icon: Icon,
    summary,
    columns,
    rows,
    footer,
}: {
    tone: PromoBannerTone;
    /** Nombre del banner; también es su `aria-label`. */
    label: string;
    icon: LucideIcon;
    summary: string;
    /** Encabezados de las tres columnas. */
    columns: readonly [string, string, string];
    rows: readonly PromoBannerRow[];
    footer?: ReactNode;
}) {
    const t = TONE[tone];
    return (
        <section
            aria-label={label}
            className={cn('flex min-w-0 flex-col gap-2.5 rounded-xl border px-4 py-3.5', t.box)}
        >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span
                    className={cn(
                        'flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.06em]',
                        t.text,
                    )}
                >
                    <Icon className="size-3.5" aria-hidden />
                    {label}
                </span>
                <span className="text-[11px] text-muted-foreground">{summary}</span>
            </div>
            {/* Si no cabe a lo ancho, la tabla se desliza dentro de su caja; cada línea se trunca. */}
            <div className="min-w-0 overflow-x-auto rounded-lg bg-card">
                <div
                    role="table"
                    aria-label={label}
                    className="grid grid-cols-[minmax(7rem,1.5fr)_minmax(6rem,1fr)_minmax(8.5rem,1.2fr)] text-[12.5px]"
                >
                    <div role="row" className="contents">
                        {columns.map((c) => (
                            <span key={c} role="columnheader" className={HEAD}>
                                {c}
                            </span>
                        ))}
                    </div>
                    {rows.map((row) => (
                        <div key={row.key} role="row" className="contents">
                            <div role="cell" className={CELL}>
                                <div className={LINE_MAIN} title={row.name}>
                                    {row.name}
                                </div>
                                <div className={LINE_SUB} title={row.detail}>
                                    {row.detail}
                                </div>
                                <div
                                    className={cn(LINE_SUB, 'flex items-center gap-1')}
                                    title={row.note}
                                >
                                    {row.badge && (
                                        <span className="flex-none rounded-full bg-promo-soft px-1.5 text-[10px] font-bold leading-4 text-promo">
                                            {row.badge}
                                        </span>
                                    )}
                                    <span className="min-w-0 truncate">{row.note}</span>
                                </div>
                            </div>
                            <div role="cell" className={CELL}>
                                <div className={LINE_MAIN} title={row.reach}>
                                    {row.reach}
                                </div>
                                <div className={LINE_SUB} title={row.reachDetail}>
                                    {row.reachDetail}
                                </div>
                            </div>
                            <div role="cell" className={CELL}>
                                <span className="flex h-[22px] flex-nowrap gap-1 overflow-hidden">
                                    {row.chips.map((chip) => {
                                        const ChipIcon = chip.icon;
                                        return (
                                            <span
                                                key={chip.key}
                                                data-chip
                                                className={cn(
                                                    'inline-flex min-w-0 max-w-full flex-none items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-bold leading-snug',
                                                    chip.tone ? CHIP_TONE[chip.tone] : t.chip,
                                                )}
                                            >
                                                {ChipIcon && (
                                                    <ChipIcon
                                                        className="size-3 flex-none"
                                                        aria-hidden
                                                    />
                                                )}
                                                <span
                                                    className="min-w-0 truncate"
                                                    title={chip.label}
                                                >
                                                    {chip.label}
                                                </span>
                                            </span>
                                        );
                                    })}
                                </span>
                                <div className={LINE_SUB} title={row.chipsDetail}>
                                    {row.chipsDetail}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            {footer && <div className="text-[11px] text-muted-foreground">{footer}</div>}
        </section>
    );
}
